import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import SquadChat from "../components/SquadChat";
import SquadMemberProfileModal from "../components/SquadMemberProfileModal";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  Crown,
  Shield,
  Trash,
  SignOut,
  CheckCircle,
  Flame,
  Copy as CopyIcon,
  ArrowRight,
  Users,
  EnvelopeSimple,
  ArrowUp,
  ArrowDown,
} from "@phosphor-icons/react";

function MemberRow({ member, isOwner, isCoOwner, isMe, rank, canManageRoster, onPromote, onDemote, canViewProfile, onViewProfile }) {
  const pct = member.total_days > 0 ? Math.round((member.completions / member.total_days) * 100) : 0;
  const nameIsClickable = canViewProfile && !isMe;
  // The name itself becomes the profile trigger when the viewer can view
  // profiles. Otherwise it's plain text.
  const NameTag = nameIsClickable ? "button" : "span";
  // Layout strategy:
  // • Mobile (default): rank+name on row 1, all metric/action chips wrap on row 2.
  // • ≥640px (sm): the metrics wrapper switches to `display:contents` so its
  //   children participate in the 6-column grid — desktop appearance unchanged.
  return (
    <div
      className={`grid gap-x-3 gap-y-2 grid-cols-[40px_1fr] sm:grid-cols-[40px_1fr_auto_auto_auto_auto] sm:items-center px-4 py-3 border-b border-border ${isMe ? "bg-volt/5" : ""}`}
      data-testid={`squad-member-${member.user_id}`}
    >
      <div className="font-display font-black text-2xl text-volt text-center self-center">#{rank}</div>
      <div className="min-w-0 self-center">
        <div className="font-display font-bold uppercase tracking-wide flex items-center gap-2 flex-wrap">
          <NameTag
            {...(nameIsClickable && {
              onClick: () => onViewProfile(member.user_id),
              "data-testid": `view-profile-${member.user_id}`,
              className: "underline decoration-volt/40 underline-offset-2 hover:text-volt focus:outline-none focus:text-volt text-left",
              title: "View this operator's assessment + improvement history",
              type: "button",
            })}
          >
            {member.name || "Operator"}
          </NameTag>
          {isOwner && (
            <span className="inline-flex items-center gap-1 text-volt text-xs font-mono uppercase">
              <Crown size={12} weight="fill" /> OWNER
            </span>
          )}
          {isCoOwner && !isOwner && (
            <span className="inline-flex items-center gap-1 text-volt text-xs font-mono uppercase border border-volt/60 px-1.5 py-0.5" data-testid={`coowner-badge-${member.user_id}`}>
              <Shield size={11} weight="fill" /> CO-OWNER
            </span>
          )}
          {isMe && <span className="label-mono text-volt">// YOU</span>}
        </div>
        <div className="text-xs text-muted-foreground">
          {member.last_completed
            ? `Last logged ${new Date(member.last_completed).toLocaleDateString()}`
            : "No sessions logged yet"}
        </div>
      </div>

      {/* Metrics + actions group. col-span-2 on mobile so it sits on its own
          row beneath rank+name; `sm:contents` on desktop so the four blocks
          drop straight back into the original 6-column grid. */}
      <div className="col-span-2 sm:contents">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 sm:block sm:text-right" data-testid={`member-completions-${member.user_id}`}>
          <div className="inline-flex items-baseline gap-1 sm:block sm:text-right">
            <span className="font-display text-lg font-black">{member.completions}</span>
            <span className="text-muted-foreground text-sm font-normal">/{member.total_days}</span>
            <span className="label-mono text-muted-foreground ml-1 sm:ml-0 sm:block">SESSIONS</span>
          </div>
          <div className="inline-flex items-center gap-1 sm:hidden">
            <Flame size={14} weight="bold" className="text-volt" />
            <span className="font-display text-lg font-black">{member.current_streak}</span>
            <span className="label-mono text-muted-foreground">STREAK</span>
          </div>
          <div className="inline-flex items-center gap-2 sm:hidden">
            <div className="w-14 h-1 bg-border">
              <div className="h-full bg-volt transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="label-mono">{pct}%</span>
          </div>
        </div>

        {/* Desktop-only blocks (kept exactly as before for ≥640px) */}
        <div className="hidden sm:block text-right">
          <div className="inline-flex items-center gap-1 font-display text-lg font-black">
            <Flame size={16} weight="bold" className="text-volt" />
            {member.current_streak}
          </div>
          <div className="label-mono text-muted-foreground">STREAK</div>
        </div>
        <div className="hidden sm:block w-16">
          <div className="h-1 bg-border">
            <div className="h-full bg-volt transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="label-mono text-center mt-1">{pct}%</div>
        </div>

        <div className="flex flex-row sm:flex-col items-end gap-1 sm:min-w-[88px]">
          {canManageRoster && !isOwner && !isCoOwner && (
            <button
              onClick={() => onPromote(member.user_id)}
              title="Promote to co-owner"
              className="text-[10px] uppercase tracking-widest border border-border px-2 py-1 inline-flex items-center gap-1 hover:border-volt hover:text-volt"
              data-testid={`promote-coowner-${member.user_id}`}
            >
              <ArrowUp size={10} weight="bold" /> Promote
            </button>
          )}
          {canManageRoster && isCoOwner && !isOwner && (
            <button
              onClick={() => onDemote(member.user_id)}
              title="Demote co-owner back to member"
              className="text-[10px] uppercase tracking-widest border border-border px-2 py-1 inline-flex items-center gap-1 hover:border-destructive hover:text-destructive"
              data-testid={`demote-coowner-${member.user_id}`}
            >
              <ArrowDown size={10} weight="bold" /> Demote
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SquadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [squad, setSquad] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [sendingBeat, setSendingBeat] = useState(false);
  const [beatResult, setBeatResult] = useState(null);
  const [anchorModalOpen, setAnchorModalOpen] = useState(false);
  const [viewProfileUserId, setViewProfileUserId] = useState(null);

  const load = () => api.get(`/squads/${id}`).then((r) => setSquad(r.data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  useEffect(() => { load(); }, [id]);

  if (err) return (
    <Layout>
      <div className="label-mono text-destructive">{err}</div>
      <Link to="/community" className="inline-flex items-center gap-2 label-mono hover:text-volt mt-4">
        <ArrowLeft size={14} weight="bold" /> Back to Community
      </Link>
    </Layout>
  );

  if (!squad) return <Layout><div className="label-mono animate-pulse">LOADING...</div></Layout>;

  const isOwner = user && squad.owner_id === user.id;
  const coOwnerIds = squad.co_owner_ids || [];
  const isCoOwner = user && coOwnerIds.includes(user.id);
  const canManage = isOwner || isCoOwner;  // Can change anchor, send heartbeat
  const myMember = squad.members?.find((m) => m.user_id === user?.id);

  const promoteCoOwner = async (memberUserId) => {
    if (!window.confirm("Promote this member to co-owner? They'll be able to change the anchor plan and send heartbeats.")) return;
    try {
      await api.post(`/squads/${id}/coowners`, { user_id: memberUserId });
      load();
    } catch (e) {
      alert("Promote failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const demoteCoOwner = async (memberUserId) => {
    if (!window.confirm("Demote this co-owner back to a regular member?")) return;
    try {
      await api.delete(`/squads/${id}/coowners/${memberUserId}`);
      load();
    } catch (e) {
      alert("Demote failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(squad.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      // navigator.clipboard may be blocked in http / permissions-denied contexts.
      // Surface the code so the user can still copy it manually.
      window.prompt("Copy the invite code manually:", squad.invite_code);
    }
  };

  const disband = async () => {
    if (!window.confirm("Disband this squad? Members keep their plan copies, but the squad is deleted.")) return;
    try {
      await api.delete(`/squads/${id}`);
      navigate("/community");
    } catch (e) {
      alert("Disband failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const sendHeartbeat = async () => {
    if (!window.confirm(`Email a weekly heartbeat to all ${squad.member_count} squad members now?`)) return;
    setSendingBeat(true); setBeatResult(null);
    try {
      const { data } = await api.post(`/squads/${id}/heartbeat`);
      setBeatResult(data);
    } catch (e) {
      setBeatResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setSendingBeat(false);
    }
  };

  const leave = async () => {
    if (!window.confirm("Leave this squad? Your plan stays in your dossier.")) return;
    await api.post(`/squads/${id}/leave`);
    navigate("/community");
  };

  return (
    <Layout>
      <Link to="/community" className="inline-flex items-center gap-2 label-mono hover:text-volt mb-6">
        <ArrowLeft size={14} weight="bold" /> Back to Community
      </Link>

      <div className="mb-8">
        <div className="label-mono text-volt mb-2 inline-flex items-center gap-2">
          <Users size={14} weight="bold" /> // SQUAD
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black mb-2">{squad.name}</h1>
        {squad.description && <p className="text-muted-foreground max-w-3xl">{squad.description}</p>}
      </div>

      {/* Stats bar */}
      <div className="grid sm:grid-cols-3 gap-px bg-border border border-border mb-6">
        <div className="bg-background p-5">
          <div className="label-mono text-volt mb-1">// MEMBERS</div>
          <div className="font-display text-3xl font-black">{squad.member_count}</div>
        </div>
        <div className="bg-background p-5 relative">
          <div className="label-mono text-volt mb-1">// ANCHOR PLAN</div>
          <div className="font-display text-base font-bold leading-tight">{squad.source_plan_name}</div>
          {canManage && (
            <button
              onClick={() => setAnchorModalOpen(true)}
              className="absolute top-3 right-3 text-xs label-mono text-muted-foreground hover:text-volt"
              data-testid="change-anchor-btn"
            >
              CHANGE →
            </button>
          )}
        </div>
        <div className="bg-background p-5 flex items-center justify-between">
          <div>
            <div className="label-mono text-volt mb-1">// INVITE CODE</div>
            <div className="font-display text-2xl font-black tracking-widest text-volt" data-testid="squad-invite-code">
              {squad.invite_code}
            </div>
          </div>
          <button
            onClick={copyInvite}
            className="border border-border px-3 py-2 hover:border-volt hover:text-volt inline-flex items-center gap-2 font-display uppercase tracking-widest text-xs"
            data-testid="copy-invite-btn"
          >
            <CopyIcon size={14} weight="bold" /> {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>

      {/* My plan link */}
      {myMember?.plan_id && (
        <Link
          to={`/plans/${myMember.plan_id}`}
          className="border border-volt bg-volt/5 p-4 mb-8 flex items-center justify-between hover:bg-volt/10"
          data-testid="my-squad-plan-link"
        >
          <div className="flex items-center gap-3">
            <CheckCircle size={20} weight="bold" className="text-volt" />
            <div>
              <div className="font-display font-bold">YOUR COPY OF THIS PLAN</div>
              <div className="text-xs text-muted-foreground">{myMember.completions}/{myMember.total_days} sessions logged · {myMember.current_streak}-day streak</div>
            </div>
          </div>
          <ArrowRight size={18} weight="bold" className="text-volt" />
        </Link>
      )}

      {/* Roster */}
      <div className="border border-border bg-card mb-8">
        <div className="border-b border-border p-4">
          <div className="label-mono text-volt mb-1">// ROSTER</div>
          <div className="font-display text-xl font-black uppercase">Member Progress</div>
        </div>
        <div>
          {squad.members.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">No members yet.</div>
          ) : (
            squad.members.map((m, i) => (
              <MemberRow
                key={m.user_id}
                member={m}
                isOwner={squad.owner_id === m.user_id}
                isCoOwner={coOwnerIds.includes(m.user_id)}
                isMe={user?.id === m.user_id}
                rank={i + 1}
                canManageRoster={isOwner}
                onPromote={promoteCoOwner}
                onDemote={demoteCoOwner}
                canViewProfile={canManage}
                onViewProfile={setViewProfileUserId}
              />
            ))
          )}
        </div>
      </div>

      {/* Squad Chat */}
      <SquadChat squad={squad} currentUserId={user?.id} isManager={canManage} />

      {/* Squad actions */}
      <div className="flex flex-wrap justify-end gap-3">
        {canManage && (
          <button
            onClick={sendHeartbeat}
            disabled={sendingBeat}
            data-testid="send-heartbeat"
            className="border border-volt text-volt px-4 py-2 inline-flex items-center gap-2 font-display uppercase tracking-widest text-xs hover:bg-volt hover:text-black disabled:opacity-50"
          >
            <EnvelopeSimple size={14} weight="bold" />
            {sendingBeat ? "Sending..." : "Send Heartbeat"}
          </button>
        )}
        {isOwner ? (
          <button
            onClick={disband}
            data-testid="disband-squad"
            className="border border-destructive text-destructive px-4 py-2 inline-flex items-center gap-2 font-display uppercase tracking-widest text-xs hover:bg-destructive hover:text-white"
          >
            <Trash size={14} weight="bold" /> Disband Squad
          </button>
        ) : (
          <button
            onClick={leave}
            data-testid="leave-squad"
            className="border border-border px-4 py-2 inline-flex items-center gap-2 font-display uppercase tracking-widest text-xs hover:border-destructive hover:text-destructive"
          >
            <SignOut size={14} weight="bold" /> Leave Squad
          </button>
        )}
      </div>

      {beatResult && (
        <div
          className={`mt-4 border px-4 py-3 text-sm ${beatResult.error ? "border-destructive text-destructive" : "border-volt bg-volt/10"}`}
          data-testid="heartbeat-result"
        >
          {beatResult.error ? (
            <>Heartbeat failed: {beatResult.error}</>
          ) : (
            <>
              Heartbeat dispatched — <b>{beatResult.sent}</b> of <b>{beatResult.total_members}</b> members emailed
              {beatResult.failed?.length > 0 && ` (${beatResult.failed.length} failed)`}.
            </>
          )}
        </div>
      )}

      {anchorModalOpen && (
        <ChangeAnchorModal
          squadId={id}
          currentPlanId={squad.source_plan_id}
          onClose={() => setAnchorModalOpen(false)}
          onChanged={() => { setAnchorModalOpen(false); load(); }}
        />
      )}

      {viewProfileUserId && (
        <SquadMemberProfileModal
          squadId={id}
          userId={viewProfileUserId}
          isOwner={squad.owner_id === viewProfileUserId}
          isCoOwner={coOwnerIds.includes(viewProfileUserId)}
          onClose={() => setViewProfileUserId(null)}
        />
      )}
    </Layout>
  );
}

function ChangeAnchorModal({ squadId, currentPlanId, onClose, onChanged }) {
  const [plans, setPlans] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [syncMembers, setSyncMembers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/plans").then((r) => {
      // Only show plans the owner has authored (the backend will reject others anyway).
      const list = (r.data || []).filter((p) => p.id !== currentPlanId);
      setPlans(list);
    }).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [currentPlanId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selectedId) { setErr("Pick a plan to anchor."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch(`/squads/${squadId}/anchor`, {
        plan_id: selectedId,
        sync_existing_members: syncMembers,
      });
      if (syncMembers) {
        alert(`Anchor updated. ${data.synced_count} member plan${data.synced_count === 1 ? "" : "s"} synced.`);
      } else {
        alert("Anchor plan updated. New joiners will copy the new plan; existing members keep their current progress.");
      }
      onChanged();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" onClick={onClose} data-testid="change-anchor-modal">
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-card border border-border p-6"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-mono text-volt">// CHANGE ANCHOR PLAN</div>
            <h2 className="font-display text-2xl font-black mt-1">Swap your squad's plan</h2>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-volt label-mono">CLOSE</button>
        </div>

        <p className="text-xs text-muted-foreground mb-4">
          New joiners will copy the new anchor plan. Existing members keep their current
          dossier unless you tick "sync existing members" below — which will replace each
          member's current squad-plan with a fresh copy of the new anchor (auto-tuned to
          their 1RMs).
        </p>

        {plans === null ? (
          <div className="label-mono animate-pulse py-6">LOADING PLANS...</div>
        ) : plans.length === 0 ? (
          <div className="border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
            You don't have any other plans saved yet. <a href="/builder" className="text-volt underline">Build a new one</a> first.
          </div>
        ) : (
          <>
            <label htmlFor="anchor-plan-select" className="label-mono mb-2 block text-muted-foreground">// PICK A PLAN YOU OWN</label>
            <select
              id="anchor-plan-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full bg-background border border-border px-3 py-2 mb-4 outline-none focus:border-volt"
              data-testid="anchor-plan-select"
            >
              <option value="">— Select —</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.type === "mesocycle" ? "MESO" : "MICRO"} · {p.weeks?.length || 0}w
                </option>
              ))}
            </select>

            <label className="flex items-start gap-2 text-xs cursor-pointer mb-4" data-testid="sync-members-toggle-label">
              <input
                type="checkbox"
                checked={syncMembers}
                onChange={(e) => setSyncMembers(e.target.checked)}
                className="mt-0.5"
                data-testid="sync-members-toggle"
              />
              <span className="text-muted-foreground">
                <span className="text-foreground font-bold">Sync existing members</span>
                <span className="block mt-1">
                  Replaces every current member's squad-plan with a fresh copy of the new
                  anchor. Their completion history on the old plan will be lost. Leave
                  unchecked to only affect new joiners.
                </span>
              </span>
            </label>
          </>
        )}

        {err && <div className="text-red-400 text-xs mb-3" data-testid="anchor-error">{err}</div>}

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="border border-border px-4 py-2 text-xs uppercase tracking-widest hover:border-volt/50" data-testid="cancel-anchor-change">
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !plans || plans.length === 0}
            className="bg-volt text-black px-4 py-2 text-xs uppercase tracking-widest font-display hover:bg-white disabled:opacity-40"
            data-testid="confirm-anchor-change"
          >
            {busy ? "Updating..." : "Update Anchor"}
          </button>
        </div>
      </form>
    </div>
  );
}
